import { isOrangePiBoard } from '@/utils/orangePi';
/**
 * Coin-box link health — signal, rejoin rate, and why it last booted.
 *
 * All of this already arrives on every NodeMCU heartbeat and is already stored;
 * neither list endpoint uses `.select()`, so the fields were on the wire long
 * before anything could read them. Nothing here needs a server change, and
 * nothing here reaches a device.
 *
 * The numbers are not safe to render raw. Each helper below exists because the
 * obvious rendering tells a lie about a real box in the fleet:
 *
 *   - `wifiReconnects` is a counter in RAM that RESETS TO ZERO on every reboot.
 *     183 rejoins over 30 days is a healthy box; 183 over one hour is a broken
 *     one. The raw count cannot distinguish them, so only a RATE is exposed.
 *   - A wired box reports `wifiRssi: 0` and `wifiChannel: 0`. Rendered naively
 *     that reads as a perfect signal on channel 0, and 76 ethernet boxes would
 *     look like the healthiest in the fleet.
 *   - Every value is as-of the last heartbeat. A box dark for three days still
 *     reports the signal it had when it died, so the caller must be able to tell
 *     "this is stale" — hence `asOf` on the returned shapes.
 */

/** How strong the AP's signal is where the NodeMCU sits. */
export interface Signal {
   /** Raw dBm, e.g. -14. */
   dbm: number;
   /** Plain-English band — the operator reads this, not the number. */
   label: 'Excellent' | 'Strong' | 'Fair' | 'Weak' | 'Critical';
   /** How worried to look: 0 = fine, 1 = watch, 2 = bad. */
   severity: 0 | 1 | 2;
   /** "-14 dBm (Excellent)" */
   title: string;
}

/**
 * Bands measured against the real fleet (2026-08-16): median RSSI is -30, so
 * MOST boxes land in Excellent. That is correct, not a scale that is too
 * generous — the NodeMCU sits a metre from its own router by design. The bands
 * earn their keep at the bottom, where -70 and below is where drops start.
 */
export const signal = (
   dbm: number | null | undefined,
   connMode?: 'ethernet' | 'wifi' | null
): Signal | null => {
   // Wired boxes report 0. Zero is "no radio", never "perfect".
   if (connMode === 'ethernet') return null;
   if (typeof dbm !== 'number' || !Number.isFinite(dbm) || dbm === 0) return null;
   // Positive or absurd values mean a board that never filled the field in.
   if (dbm > 0 || dbm < -120) return null;

   const [label, severity]: [Signal['label'], Signal['severity']] =
      dbm >= -30
         ? ['Excellent', 0]
         : dbm >= -55
           ? ['Strong', 0]
           : dbm >= -67
             ? ['Fair', 1]
             : dbm >= -80
               ? ['Weak', 2]
               : ['Critical', 2];

   return { dbm, label, severity, title: `${dbm} dBm (${label})` };
};

/** How often the box loses its connection and rejoins, per hour of uptime. */
export interface RejoinRate {
   /** Rejoins per hour, e.g. 41.88. */
   perHour: number;
   /** Rounded for display — "41.9", or "0.1". */
   display: string;
   severity: 0 | 1 | 2;
   title: string;
}

/**
 * Below this much uptime the rate is meaningless — a box up four minutes that
 * rejoined twice is not doing 30/hour, it just booted. Show nothing instead of
 * a number that would send someone to site.
 */
export const RATE_MIN_UPTIME_SEC = 1800; // 30 minutes

/**
 * ⚠️ A high rate means "this box keeps losing its connection" — NOT necessarily
 * "bad WiFi". The firmware bumps the same counter when the SERVER stops
 * answering and it forces a rejoin. 5D ALCARDE sat at 41.9/hr on a -14 dBm
 * signal because its router's agent was stuck, not because of the radio.
 * The title says so, so nobody reads the number as a radio verdict.
 */
export const rejoinRate = (
   reconnects: number | null | undefined,
   uptimeSec: number | null | undefined,
   connMode?: 'ethernet' | 'wifi' | null
): RejoinRate | null => {
   if (connMode === 'ethernet') return null;
   if (typeof reconnects !== 'number' || !Number.isFinite(reconnects) || reconnects < 0)
      return null;
   if (typeof uptimeSec !== 'number' || !Number.isFinite(uptimeSec)) return null;
   if (uptimeSec < RATE_MIN_UPTIME_SEC) return null;

   const perHour = reconnects / (uptimeSec / 3600);
   const severity: RejoinRate['severity'] = perHour >= 5 ? 2 : perHour >= 1 ? 1 : 0;
   const display = perHour >= 10 ? perHour.toFixed(0) : perHour.toFixed(1);

   return {
      perHour,
      display,
      severity,
      title:
         `${reconnects} reconnects over ${(uptimeSec / 3600).toFixed(1)}h of uptime. ` +
         `A high rate means the box keeps losing its connection — that can be the ` +
         `WiFi OR the server not answering, so check the router's agent too.`,
   };
};

/**
 * Why the NodeMCU last booted. This is the column that separates a POWER
 * problem from a NETWORK one without anyone driving to the shop:
 *
 *   "Power On"           — the mains dropped, or somebody unplugged it.
 *   "Software/System restart" — IT restarted ITSELF. Our 3-minute watchdog gave
 *                          up waiting for the server. Nobody touched the box.
 *   "Hardware Watchdog"  — the chip hung and the silicon reset it.
 *   "External System"    — the reset line was pulled (normal after a flash).
 */
export interface BootReason {
   raw: string;
   label: string;
   /** What it actually tells you, in one line. */
   meaning: string;
   severity: 0 | 1 | 2;
}

export const bootReason = (raw: string | null | undefined): BootReason | null => {
   const value = (raw || '').trim();
   if (!value) return null;

   const map: Record<string, Omit<BootReason, 'raw'>> = {
      'Power On': {
         label: 'Power On',
         meaning: 'Mains power was lost or the box was unplugged.',
         severity: 1,
      },
      'Software/System restart': {
         label: 'Self-restart',
         meaning:
            'The box restarted itself after 3 minutes with no answer from the server. ' +
            'Nobody touched it — check the router, not the box.',
         severity: 2,
      },
      'Hardware Watchdog': {
         label: 'Hardware watchdog',
         meaning: 'The chip stopped responding and reset itself.',
         severity: 2,
      },
      'External System': {
         label: 'Reset',
         meaning: 'The reset line was pulled — normal right after a re-flash.',
         severity: 0,
      },
      'Exception': {
         label: 'Crash',
         meaning: 'The firmware crashed.',
         severity: 2,
      },
   };

   const hit = map[value];
   if (hit) return { raw: value, ...hit };
   return { raw: value, label: value, meaning: '', severity: 0 };
};

/** WiFi or Ethernet — without it the signal column is nonsense for wired boxes. */
export const linkType = (
   connMode: 'ethernet' | 'wifi' | null | undefined
): 'WiFi' | 'Ethernet' | null =>
   connMode === 'wifi' ? 'WiFi' : connMode === 'ethernet' ? 'Ethernet' : null;

/**
 * The 2.4GHz channel the box settled on. Worth surfacing: 257 of 263 Ruijie
 * boxes sit on channel 1 because our bootstrap never sets one, so they all
 * share the air with each other and with every neighbour.
 */
export const channel = (
   ch: number | null | undefined,
   connMode?: 'ethernet' | 'wifi' | null
): number | null => {
   if (connMode === 'ethernet') return null;
   return typeof ch === 'number' && ch > 0 ? ch : null;
};

/**
 * Which antenna the NodeMCU is on, and whether it is the right one.
 *
 * A wireless NodeMCU joins by SSID NAME alone and takes whichever access point
 * is loudest, without checking whose it is. Proven on hardware twice: a box sat
 * on an unrelated AP at -7 dBm, perfectly associated and completely unreachable,
 * and a box came back from a shop power cut on the operator's own extender
 * instead of its router. See [[smartfi_open_ssid_stranger_capture_risk]].
 *
 * The router reports the addresses of its OWN radios, so this compares where the
 * box actually is against that list.
 *
 * ⚠️ WORDING MATTERS. `away` means "not on its own router's antenna" — NOT
 * "captured". The operator's own extender is separate hardware with its own
 * address, so a completely legitimate shop reads as `away`. Calling that a
 * capture accuses a customer of being hacked because they bought a range
 * extender.
 */
export type AntennaState = 'home' | 'away' | 'unknown' | 'external';

export interface Antenna {
   state: AntennaState;
   /** The address the box is on, for display. Empty when it reports none. */
   bssid: string;
   /** Short label for a cell. */
   label: string;
   /** The full explanation, for a tooltip or a card. */
   detail: string;
   severity: 0 | 1 | 2;
}

const sameMac = (a: string, b: string) =>
   a.trim().toUpperCase().replace(/-/g, ':') ===
   b.trim().toUpperCase().replace(/-/g, ':');

/** Boards with no WiFi radio of their own: the NodeMCU can only ever be on
 *  somebody else's access point, so "not on this router's antenna" is the
 *  normal state there, never a fault. */
const boardHasNoRadio = (hwBoard?: string | null) => {
   const hw = (hwBoard || '').toLowerCase();
   return isOrangePiBoard(hw) || hw.startsWith('x86-64');
};

export const antenna = (
   nodeMcu: { bssid?: string; connMode?: string; pinFellBack?: boolean } | undefined,
   router:
      | { apBssids?: { bssid: string; freq: number }[]; hwBoard?: string | null }
      | undefined
): Antenna | null => {
   const on = (nodeMcu?.bssid || '').trim();
   // A wired box has no antenna at all — nothing to say, not even "unknown".
   if (nodeMcu?.connMode === 'ethernet') return null;
   if (!on) return null;

   // A mini PC or OrangePi has no radio: the NodeMCU joined the shop's own
   // access point. Say which one; do not call it "unknown" or ask for a
   // router update that would change nothing.
   if (boardHasNoRadio(router?.hwBoard)) {
      return {
         state: 'external',
         bssid: on,
         label: 'Shop WiFi',
         detail:
            'This router has no WiFi radio of its own, so the NodeMCU is on the ' +
            'shop’s access point. That is the normal setup for this hardware.',
         severity: 0,
      };
   }

   const own = router?.apBssids || [];
   // ⚠️ An empty list is UNKNOWN, never "away". A router on an older agent has
   // not told us its radios — it has not told us the box is somewhere wrong.
   // Reading silence as "away" would flag every box on every un-updated router.
   if (own.length === 0) {
      return {
         state: 'unknown',
         bssid: on,
         label: 'Unknown',
         detail:
            'This router has not reported which antennas are its own, so we ' +
            'cannot say whether the NodeMCU is on the right one. It needs a ' +
            'router update.',
         severity: 0,
      };
   }

   if (own.some((r) => r.bssid && sameMac(r.bssid, on))) {
      return {
         state: 'home',
         bssid: on,
         label: 'Own router',
         detail: 'The NodeMCU is on this router’s own antenna.',
         severity: 0,
      };
   }

   // The box itself says whether it CHOSE to be here or gave up trying to reach
   // its own antenna. That difference is the whole point of the flag: one is a
   // box that needs looking at, the other is a router that does.
   const gaveUp = nodeMcu?.pinFellBack === true;
   return {
      state: 'away',
      bssid: on,
      label: gaveUp ? 'Off its antenna' : 'Another antenna',
      detail: gaveUp
         ? 'The NodeMCU could not reach its own router’s antenna and joined ' +
           'another one with the same WiFi name. It keeps trying to go back, ' +
           'so this usually clears itself once the router is reachable again.'
         : 'The NodeMCU is on an antenna that does not belong to this router. ' +
           'That is normally the operator’s own extender or a neighbour using ' +
           'the same WiFi name.',
      severity: gaveUp ? 1 : 2,
   };
};
